import React, { useCallback, useContext, useMemo } from "react";
import Highlighter from "react-highlight-words";
import { Link } from "react-router-dom";

import { faBook, faCalendar, faLock, faSparkle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faPrivate } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import moment from "moment";

import type { AssigneeCompletionData } from "~/actionItems/ActionItemCompletionMenu";
import ActionItemProgressPill from "~/actionItems/ActionItemProgressPill";
import { Views } from "~/actionItems/constants";
import { NoteBlockStatus, StatusCondition } from "~/actionItems/enum";
import { ActionItemsContext } from "~/actionItems/newActionItems/ActionItemsContext";
import type { ActionItem } from "~/actionItems/types";
import { Date } from "~/common";
import { formatTimestamp } from "~/meetingRecorder/utils";
import { Tooltip } from "~/ui";

import { openTranscriptModal } from "../meetingRecorder/TranscriptModal";
import { encodeNoteUrl, toggleNoteBlockStatus } from "./utils";

import styles from "./ActionItemMeta.module.css";

function SeriesNameLink({ children, item }: React.PropsWithChildren<{ item: ActionItem }>) {
	const { note, recordingId, recordingOffset } = item;
	const { view, inPanel } = useContext(ActionItemsContext);

	const openRecapAtTimestamp = useMemo(() => {
		if (!recordingId || !recordingOffset) return null;

		return () =>
			openTranscriptModal({
				recordingId,
				noteId: note.id,
				timestamp: recordingOffset,
			});
	}, [note.id, recordingId, recordingOffset]);

	const linkLocation = useMemo(() => {
		const hash = encodeNoteUrl({
			noteId: note.id,
			blockKey: item.key,
			privateNote: note.type === "private",
		});

		if (view === Views.STREAM_TAB) {
			return {
				pathname: "../notes/",
				hash,
			};
		}

		if (note.stream.url && !note.stream.isTodoStream) {
			return {
				pathname: `${note.stream.url}notes/`,
				hash,
			};
		}

		return null;
	}, [note, item.key, view]);

	if (openRecapAtTimestamp) {
		return (
			<button
				className={cx(styles.data, styles.streamName, styles.meetingsLink, { [styles.metaTextInPanel]: inPanel })}
				// TODO: open meeting modal in new UX
				onClick={openRecapAtTimestamp}
			>
				{children}
			</button>
		);
	}

	if (linkLocation) {
		return (
			<Link
				// TODO: In new ux, head to companion mode alternative
				to={linkLocation}
				className={cx(styles.data, styles.streamName, styles.meetingsLink, { [styles.metaTextInPanel]: inPanel })}
			>
				{children}
			</Link>
		);
	}

	return <span className={cx(styles.data, styles.streamName, { [styles.metaTextInPanel]: inPanel })}>{children}</span>;
}

/** Meta info to show when not in the panel  */
function AdditionalInfo({ children, showIf }: React.PropsWithChildren<{ showIf: boolean }>) {
	const { inPanel } = useContext(ActionItemsContext);

	if (inPanel || !showIf) return null;

	return (
		<>
			<strong>·</strong>
			{children}
		</>
	);
}

type ActionItemMetaProps = {
	item: ActionItem;
	assignees: Array<AssigneeCompletionData>;
	highlightedText: string[];
	className?: string;
	inAISuggestions?: boolean;
};

export default function ActionItemMeta({
	item,
	assignees,
	highlightedText,
	className,
	inAISuggestions,
}: ActionItemMetaProps) {
	const { id, createdAt, note, hasAccessToNote, statusCondition, aiGenerated, recordingId, recordingOffset } = item;
	const { inPanel, onUpdateItemsAttrs, viewer } = useContext(ActionItemsContext);
	const user = viewer?.user;

	let icon = faCalendar;
	if (note?.type === "private") icon = faPrivate;
	else if (note?.isLocked) icon = faLock;
	else if (note?.stream?.type === "personal") icon = faBook;

	const streamName = note.stream.displayName || "Untitled note series";
	const timestamp = recordingId && recordingOffset && formatTimestamp(recordingOffset, null);
	const seriesNameAndIcon = (
		<>
			<FontAwesomeIcon icon={icon} />
			&nbsp;
			<Highlighter
				highlightClassName="prose-highlight"
				searchWords={highlightedText}
				textToHighlight={streamName}
				autoEscape
				title={streamName}
			/>
			{timestamp && <strong>&nbsp;[{timestamp}]</strong>}
		</>
	);

	const showAssignYourself = useMemo(
		() => !Boolean(assignees.find(assignee => assignee.id === user?.id)),
		[user, assignees],
	);

	const onToggleForAssignee = useCallback(
		(assignee: AssigneeCompletionData) => {
			if (!assignees) return;
			const newAssignees = assignees.map(currentAssignee => {
				return {
					id: currentAssignee.id,
					status:
						currentAssignee.id !== assignee.id
							? currentAssignee.status
							: toggleNoteBlockStatus(currentAssignee.status as NoteBlockStatus),
				};
			});
			onUpdateItemsAttrs([id], { assignees: newAssignees });
		},
		[id, assignees, onUpdateItemsAttrs],
	);

	const onAssignYourself = useCallback(() => {
		if (!user) return;
		const newAssignees = [
			...assignees,
			{
				id: user.id,
				status: NoteBlockStatus.NOT_DONE,
			},
		];
		onUpdateItemsAttrs([id], { assignees: newAssignees, done: false, wontDo: false });
	}, [user, id, assignees, onUpdateItemsAttrs]);

	return (
		<div className={cx(styles.wrapper, className)}>
			{!hasAccessToNote ? (
				<Tooltip content="You don't have access to this note series." placement="bottom" disabled={inPanel}>
					<span className={cx(styles.data, { [styles.metaTextInPanel]: inPanel })}>{seriesNameAndIcon}</span>
				</Tooltip>
			) : note.stream.isTodoStream ? (
				<Tooltip content="Created from action items page" placement="bottom" disabled={inPanel}>
					<span className={styles.data}>No series</span>
				</Tooltip>
			) : (
				<Tooltip content="Locked notes cannot be edited." placement="bottom" disabled={inPanel || !note?.isLocked}>
					<SeriesNameLink item={item}>{seriesNameAndIcon}</SeriesNameLink>
				</Tooltip>
			)}

			<AdditionalInfo showIf={!note?.stream?.isTodoStream}>
				<Date as="span" className={styles.data} usePopup useNumbers>
					{moment.min(moment(createdAt), moment())}
				</Date>
			</AdditionalInfo>

			{/* It's redundant to show AI-Detected in the AI Suggestions section, so we hide this.
				It should still show for AI action items that have been accepted. */}
			<AdditionalInfo showIf={aiGenerated && !inAISuggestions}>
				<span className={styles.data}>
					<FontAwesomeIcon icon={faSparkle} />
					&nbsp;AI-Detected
				</span>
			</AdditionalInfo>

			<AdditionalInfo showIf={statusCondition === StatusCondition.ALL && assignees.length > 1}>
				<ActionItemProgressPill
					assignees={assignees}
					showAssignYourself={showAssignYourself}
					onToggle={onToggleForAssignee}
					onAssignYourself={onAssignYourself}
					readonlyWithLongText
					className={cx(styles.data, styles.metaProgressPill)}
				/>
			</AdditionalInfo>
		</div>
	);
}
